import { Stopwatch } from '@tunein/web-utils';
import { noop } from 'es-toolkit';
import { api } from '../../../singletons';
import { debug } from '../../../utils';
import { BUFFER_DURATION_REPORTING_THRESHOLD, commands, DURATION_THRESHOLD_IN_SECONDS, FAILURE_STATUS_REPORT_THRESHOLD, PERIODIC_REPORT_INITIAL_INTERVAL_REPEAT_CONFIG, PERIODIC_REPORT_INITIAL_INTERVAL_SECONDS_CONFIG, PERIODIC_REPORT_INTERVAL_SECONDS, PERIODIC_REPORT_INTERVAL_SECONDS_CONFIG, QUALIFIED_REPORT_INTERVAL_MS, triggers, } from './constants';
import { createStreamMessage } from './utils';
export class ListenTimeReporter {
    #timeoutId = null;
    #metrics = {
        sessionStartTime: 0,
        playingStartTime: 0,
        seekTime: 0,
    };
    #baseParams = {};
    #periodicReportInterval;
    #initialPeriodicReportInterval;
    #initialPeriodicReportRepeat;
    #isInitialPeriodicReportingEnabled;
    #periodicReportCount = 0;
    #onlyHttpStreams;
    #isBuffering = false;
    #isQualified = false;
    #didReportSuccess = false;
    // Wall clock stopwatch to measure listening time
    #playbackDurationStopwatch;
    // Measures the total playback time taking into account the playback ratio
    #streamOffsetStopwatch;
    // Measures pause and buffering duration
    #pauseStopwatch;
    constructor() {
        this.#periodicReportInterval = api.getConfigSetting(PERIODIC_REPORT_INTERVAL_SECONDS_CONFIG, PERIODIC_REPORT_INTERVAL_SECONDS);
        // See const definitions for a description of the initial periodic report interval and repeat.
        this.#initialPeriodicReportInterval = api.getConfigSetting(PERIODIC_REPORT_INITIAL_INTERVAL_SECONDS_CONFIG, 0);
        this.#initialPeriodicReportRepeat = api.getConfigSetting(PERIODIC_REPORT_INITIAL_INTERVAL_REPEAT_CONFIG, 0);
        this.#isInitialPeriodicReportingEnabled = Boolean(this.#initialPeriodicReportInterval && this.#initialPeriodicReportRepeat);
        // Disable qualified report (by setting this to true) if initial periodic reporting is enabled
        this.#isQualified = this.#isInitialPeriodicReportingEnabled;
        this.#playbackDurationStopwatch = new Stopwatch();
        this.#streamOffsetStopwatch = new Stopwatch();
        this.#pauseStopwatch = new Stopwatch();
    }
    #report(options) {
        const { command, reportData } = options;
        // all reports should stop periodic timer
        this.#stopPeriodicReporter();
        const params = {
            c: command,
            ...this.#baseParams,
            ...reportData?.params,
        };
        const event = {
            params,
            body: reportData?.body,
        };
        api.telemetry.reportEvent(event).catch(noop);
    }
    #sendTimeReport = (trigger) => {
        const listenDuration = this.#playbackDurationStopwatch.getTime();
        const { seekTime } = this.#metrics;
        const streamOffset = this.#streamOffsetStopwatch.getTime() + seekTime;
        // content offset represents expected stream position against wall clock
        // when no interruption and playback rate 1
        // The value will be negative if the playback represents the future relative to the expected position.
        const contentOffset = Date.now() - this.#metrics.playingStartTime - streamOffset;
        this.#report({
            command: commands.timev3,
            reportData: {
                trigger,
                body: {
                    reports: [
                        {
                            trigger: trigger,
                            duration: Math.round(listenDuration / 1000),
                            streamOffset: Math.round(streamOffset / 1000),
                            contentOffset: Math.round(contentOffset / 1000),
                            // listen offset is always zero because this only tracks intentionally delayed reports, which we do not implement
                            listenOffset: 0,
                        },
                    ],
                },
            },
        });
        // after each report reset or restart playback duration stopwatch
        if (this.#pauseStopwatch.isRunning) {
            this.#playbackDurationStopwatch.reset();
        }
        else {
            this.#playbackDurationStopwatch.start();
        }
    };
    #reportBufferEnd() {
        this.#isBuffering = false;
        const pauseDuration = this.#pauseStopwatch.getTime();
        // is duration is lower than threshold, don't report, but do offset the periodic timer
        if (pauseDuration < BUFFER_DURATION_REPORTING_THRESHOLD) {
            this.#stopPeriodicReporter();
            const nextPeriodicIntervalOffset = this.#playbackDurationStopwatch.getTime();
            this.#startPeriodicReporter(nextPeriodicIntervalOffset);
            debug.verbose('[LTR][BUFFER END][NOOP]');
            return;
        }
        this.#report({
            command: commands.buffer,
            reportData: { params: { duration: pauseDuration } },
        });
        debug.verbose('[LTR][BUFFER END]');
    }
    #onPeriodicReporterInterval = () => {
        debug.verbose('[LTR][PERIODIC]', Date.now());
        const wasQualified = this.#isQualified;
        if (!wasQualified) {
            this.#isQualified = true;
        }
        this.#periodicReportCount++;
        this.#sendTimeReport(triggers.periodic);
        this.#startPeriodicReporter(wasQualified ? 0 : QUALIFIED_REPORT_INTERVAL_MS);
    };
    #selectPeriodicReportInterval() {
        if (!this.#isQualified) {
            return QUALIFIED_REPORT_INTERVAL_MS;
        }
        if (this.#isInitialPeriodicReportingEnabled &&
            this.#periodicReportCount < this.#initialPeriodicReportRepeat) {
            return this.#initialPeriodicReportInterval * 1000;
        }
        return this.#periodicReportInterval * 1000;
    }
    #startPeriodicReporter(intervalOffsetMs = 0) {
        if (this.#timeoutId) {
            return;
        }
        const intervalMs = this.#selectPeriodicReportInterval();
        const intervalMsWithOffset = Math.max(intervalMs - intervalOffsetMs, 0);
        this.#timeoutId = setTimeout(this.#onPeriodicReporterInterval, intervalMsWithOffset || intervalMs);
    }
    #stopPeriodicReporter() {
        if (this.#timeoutId) {
            clearTimeout(this.#timeoutId);
            this.#timeoutId = null;
        }
    }
    #reportStreamStatus = (data, error) => {
        const streamMessage = createStreamMessage(data);
        const params = {
            onlyHttpStreams: this.#onlyHttpStreams,
            format: data.format,
        };
        if (error) {
            params.error = 1000;
            params.message = `${streamMessage}|${error.message}`;
        }
        else {
            // duration here measures the time between session start and play success
            params.duration = Date.now() - this.#metrics.sessionStartTime;
            params.successMessage = streamMessage;
        }
        this.#report({
            command: commands.stream,
            reportData: { params },
        });
    };
    /**
     * Send a report when the stream succeeds
     */
    #reportStreamPlaybackSuccess(streamGuideId, streamStatusData) {
        this.#didReportSuccess = true;
        this.#baseParams.streamGuideId = streamGuideId;
        // on initial playback set the timestamp
        if (!this.#metrics.playingStartTime) {
            this.#metrics.playingStartTime = Date.now();
        }
        // start playback and duration stopwatches after successful play
        this.#streamOffsetStopwatch.start();
        this.#playbackDurationStopwatch.start();
        this.#reportStreamStatus(streamStatusData);
    }
    #setListeningStopwatchesMode(action) {
        switch (action) {
            case 'start':
                this.#streamOffsetStopwatch.resume();
                this.#playbackDurationStopwatch.start();
                break;
            case 'resume':
                this.#streamOffsetStopwatch.resume();
                this.#playbackDurationStopwatch.resume();
                break;
            default:
                this.#streamOffsetStopwatch.stop();
                this.#playbackDurationStopwatch.stop();
                break;
        }
    }
    #reset() {
        this.#didReportSuccess = false;
        this.#stopPeriodicReporter();
        this.#playbackDurationStopwatch.stop();
        this.#streamOffsetStopwatch.stop();
        this.#pauseStopwatch.stop();
        this.#playbackDurationStopwatch.reset();
        this.#streamOffsetStopwatch.reset();
        this.#pauseStopwatch.reset();
    }
    startSession(sessionData) {
        const { guideId: id, itemToken } = sessionData.guideItem;
        this.#reset();
        // Disable qualified report (by setting this to true) if initial periodic reporting is enabled
        this.#isQualified = this.#isInitialPeriodicReportingEnabled;
        this.#periodicReportCount = 0;
        this.#metrics.sessionStartTime = Date.now();
        this.#baseParams = {
            id,
            listenId: sessionData.listenId,
            itemToken: itemToken,
        };
        this.#metrics.playingStartTime = 0;
    }
    onStartStreamLoad({ position, onlyHttpStreams, }) {
        this.#metrics.seekTime = position ? position * 1000 : 0;
        this.#onlyHttpStreams = onlyHttpStreams;
    }
    /**
     * Safely resumes periodic reporter from paused or buffering states. Does nothing if periodic reporter is
     */
    maybeResumePeriodicReporter = () => {
        if (!this.#didReportSuccess) {
            return;
        }
        if (!this.#pauseStopwatch.isRunning) {
            this.#startPeriodicReporter();
            return;
        }
        this.#pauseStopwatch.stop();
        if (this.#isBuffering) {
            // Resume the stopwatches after buffering
            this.#setListeningStopwatchesMode('resume');
            this.#reportBufferEnd();
        }
        else {
            // Restart the stopwatches after pause
            this.#setListeningStopwatchesMode('start');
        }
        this.#startPeriodicReporter();
    };
    /**
     * Sets buffer start time, which will be processed on next time update via setPlayerMetrics().
     */
    setBufferStartTime() {
        if (!this.#didReportSuccess) {
            return;
        }
        debug.verbose('[LTR][BUFFER START]');
        this.#stopPeriodicReporter();
        this.#pauseStopwatch.start();
        this.#setListeningStopwatchesMode('stop');
        this.#isBuffering = true;
    }
    /**
     * Send a report when the stream has been paused
     */
    reportPause() {
        this.#pauseStopwatch.start();
        this.#setListeningStopwatchesMode('stop');
        this.#sendTimeReport(triggers.pause);
    }
    /**
     * Stop the periodic logging, reset timers and send a report when the stream has been stopped
     */
    reportStop() {
        if (!this.#didReportSuccess) {
            this.#reset();
            return;
        }
        // A paused stream is sufficient for reporting purposes. Don't need to send stop.
        if (!this.#pauseStopwatch.isRunning) {
            this.#sendTimeReport(triggers.stop);
        }
        this.#reset();
    }
    /**
     * Stop the periodic logging, reset timers and send a report when the stream has ended
     */
    reportEnd() {
        if (this.#didReportSuccess) {
            this.#sendTimeReport(triggers.end);
            this.#reset();
        }
    }
    /**
     * Send a report when the stream fails
     */
    reportFailure(streamId, data, error) {
        this.#baseParams.streamGuideId = streamId;
        const { playingStartTime } = this.#metrics;
        const listenDuration = this.#playbackDurationStopwatch.getTime();
        // only send time report if playback was successful (playing start time timestamp is set on success)
        if (playingStartTime) {
            this.#sendTimeReport(triggers.fail);
        }
        // only send time report if playback failed or failure occurred within listen duration threshold
        if (!playingStartTime || listenDuration < FAILURE_STATUS_REPORT_THRESHOLD) {
            this.#reportStreamStatus(data, error);
        }
        this.#reset();
    }
    onPlaying(streamGuideId, streamStatusData, playbackRate) {
        if (this.#didReportSuccess) {
            this.maybeResumePeriodicReporter();
            return;
        }
        this.setPlaybackRate(playbackRate);
        this.#reportStreamPlaybackSuccess(streamGuideId, streamStatusData);
        this.#startPeriodicReporter();
    }
    /**
     * Reports a `shift_ff` if the newPosition is greater than the current playbackPosition
     * Reports a `shift_rw` if the newPosition is less than the current playbackPosition
     * @param {number} newPlaybackPosition The next position of the stream in seconds
     * @param {number} playbackPosition The current position of the stream in seconds
     * @param {number} playbackDuration The duration of the stream in seconds
     */
    reportShift = (newPlaybackPosition, playbackPosition, playbackDuration) => {
        if (newPlaybackPosition < 0 || newPlaybackPosition > playbackDuration) {
            return;
        }
        const playbackPositionDiff = newPlaybackPosition - playbackPosition;
        const meetsReportDurationThreshold = Math.abs(playbackPositionDiff) >= DURATION_THRESHOLD_IN_SECONDS;
        // Conditionally report, but content offset must always be updated afterward, regardless if there was a report or not.
        if (!this.#pauseStopwatch.isRunning && meetsReportDurationThreshold) {
            const trigger = playbackPositionDiff > 0 ? triggers.shiftFastForward : triggers.shiftRewind;
            this.#sendTimeReport(trigger);
        }
        // Important: setting seek time has to be done after the shift report is sent.
        this.#metrics.seekTime += playbackPositionDiff * 1000;
    };
    /**
     * Set stream offset stopwatch ratio based on playback rate to measure proper time
     * @param playbackRate stream playback rate
     */
    setPlaybackRate(playbackRate) {
        this.#streamOffsetStopwatch.setRatio(playbackRate);
    }
}
